import anime from "animejs";
import $ from "cash-dom";

let openMenu = null;

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function isActuallyVisible(el) {
  // "hidden" Tailwind adds display:none so offsetParent === null
  // Also protect against elements inside hidden parents.
  return !!(el && (el.offsetParent !== null || el === document.activeElement));
}

function getFocusable(containerEl) {
  if (!containerEl) return [];
  const selectors = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  const nodes = Array.from(containerEl.querySelectorAll(selectors));
  return nodes.filter((n) => isActuallyVisible(n));
}

function focusFirstInMenuPanel(panelEl) {
  if (!panelEl) return;

  // Prefer submenu labels first (your “Poistenie vozidla”, …)
  const label = panelEl.querySelector(".nav-menu-label");
  if (label && typeof label.focus === "function") {
    label.focus();
    return;
  }

  // Fallback: first focusable
  const focusables = getFocusable(panelEl);
  if (focusables[0] && typeof focusables[0].focus === "function") {
    focusables[0].focus();
  }
}

function focusFirstInActiveCategory($menuRoot) {
  const $targets = $menuRoot.find(".nav-menu-category");
  const $active = $targets.filter(":not(.hidden)").first();
  if (!$active.length) return;

  const focusables = getFocusable($active.get(0));
  if (focusables[0] && typeof focusables[0].focus === "function") {
    focusables[0].focus();
  }
}

function focusNextTopLevelAfter(triggerEl) {
  if (!triggerEl) return;

  // Prefer siblings within the same nav row/container
  const container = triggerEl.parentElement;
  if (!container) return;

  const focusables = getFocusable(container);
  const idx = focusables.indexOf(triggerEl);
  const next = idx >= 0 ? focusables[idx + 1] : null;

  if (next && typeof next.focus === "function") next.focus();
}

function closeMenuPanelAndFocusNext(panelEl) {
  if (!panelEl) return;

  const triggerEl = panelEl.__triggerEl;
  if (triggerEl) {
    // Close using the same pathway as user interaction so state stays consistent
    $(triggerEl).trigger("closeMenu");
  }

  // Ensure global state is reset
  openMenu = null;

  // Move focus to the next top-level nav item (e.g. “O nás”)
  focusNextTopLevelAfter(triggerEl);
}

function bindNavMenuCloseButtons() {
  $(document).on("click", ".nav-menu-close", function (e) {
    e.preventDefault();
    e.stopPropagation();

    const panelEl = $(this).closest(".nav-menu").get(0);
    if (panelEl) closeMenuPanelAndFocusNext(panelEl);
  });
}

/* -------------------------------------------------------------------------- */
/* Desktop top-level item: "Poistenia", "O nás", ...                           */
/* -------------------------------------------------------------------------- */

export const navMenuItemComponent = async () => {
  $(".nav-menu-item[data-target-id]").each((n, el) => {
    const $root = $(el);
    const targetId = $root.attr("data-target-id");
    const $target = targetId ? $(`#${targetId}`) : $([]);
    let isOpen = false;

    const animateOpen = () => {
      $root.toggleClass("text-black text-red");
      $root.attr("aria-expanded", "true");

      // store backlink so submenu can shift-tab back to opener
      if ($target.length) {
        $target.get(0).dataset.triggerSelector = "";
        // store a direct reference (safe) for our handlers
        $target.get(0).__triggerEl = $root.get(0);
      }

      anime({
        targets: $target.get(),
        height: 573,
        duration: 250,
        easing: "easeOutQuad",
        complete: () => {
          // ✅ After open, move focus into submenu (helps SR + solves first jump)
          focusFirstInMenuPanel($target.get(0));
        },
      });
    };

    const animateClose = (duration = 250) => {
      $root.toggleClass("text-black text-red");
      $root.attr("aria-expanded", "false");
      anime({
        targets: $target.get(),
        height: 0,
        duration,
        easing: "easeOutQuad",
      });
    };

    const toggleOpen = () => {
      if (isOpen) {
        animateClose();
        isOpen = false;
        openMenu = null;
      } else {
        if (openMenu && openMenu !== $root) {
          openMenu.trigger("closeMenu");
        }
        animateOpen();
        isOpen = true;
        openMenu = $root;
      }
    };

    $root.on("closeMenu", () => {
      if (isOpen) {
        animateClose(100);
        isOpen = false;
      }
    });

    // Mouse activation
    $root.on("click", () => toggleOpen());

    // Keyboard activation (Enter/Space) + Tab order enforcement (5.15 + 5.16)
    $root.on("keydown", (e) => {
      // activate item
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleOpen();
        return;
      }

      // ✅ critical: when open and user presses TAB on the top item,
      // jump into submenu instead of going to the next top-level item in DOM
      if (e.key === "Tab" && !e.shiftKey && isOpen) {
        e.preventDefault();
        focusFirstInMenuPanel($target.get(0));
      }
    });
  });
};

/* -------------------------------------------------------------------------- */
/* Submenu inside opened panel: labels + categories                            */
/* -------------------------------------------------------------------------- */

export const navMenuComponent = async () => {
  $(".nav-menu").each((n, el) => {
    const $root = $(el);
    const $labels = $root.find(".nav-menu-label");
    const $labelsRoot = $root.find(".nav-menu-labels-root");
    const $activeLabelBg = $root.find(".nav-menu-active-label-bg");
    const $targets = $root.find(".nav-menu-category");

    const activate = (labelEl) => {
      const $label = $(labelEl);
      const targetId = $label.attr("data-target-id");
      if (!targetId) return;

      const $target = $targets.filter(`#${targetId}`);
      if ($target.length === 0) return;

      $labels
        .removeClass("text-black font-semibold")
        .addClass("text-gray-dark font-normal");
      $label
        .addClass("text-black font-semibold")
        .removeClass("text-gray-dark font-normal");

      $targets.addClass("hidden");
      $target.removeClass("hidden");

      anime({
        targets: $activeLabelBg.get(),
        top: $label.offset().top - $labelsRoot.offset().top,
        duration: 250,
        easing: "easeOutQuad",
      });
    };

    const focusActiveLabel = () => {
      // whichever label currently has the “active” styling
      const activeLabel =
        $labels.filter((_, node) => $(node).hasClass("font-semibold")).get(0) ||
        $labels.get(0);

      if (activeLabel && typeof activeLabel.focus === "function") {
        activeLabel.focus();
      }
    };

    const focusNextLabel = () => {
      const activeCat = $targets.filter(":not(.hidden)").first();
      if (!activeCat.length) return false;

      const activeCatId = activeCat.attr("id");
      const currentLabelEl = $labels
        .filter((_, node) => $(node).attr("data-target-id") === activeCatId)
        .get(0);

      if (!currentLabelEl) return false;

      const labelEls = $labels.get();
      const idx = labelEls.indexOf(currentLabelEl);
      const nextLabel = labelEls[idx + 1];

      if (nextLabel && typeof nextLabel.focus === "function") {
        nextLabel.focus();
        activate(nextLabel);
        return true;
      }

      return false;
    };

    // Mouse hover
    $labels.on("mouseenter", (e) => activate(e.currentTarget));

    // Mouse click
    $labels.on("click", (e) => {
      e.preventDefault();
      activate(e.currentTarget);
    });

    // Focus changes active category (Tabbing labels)
    $labels.on("focus", (e) => activate(e.currentTarget));

    // Label keyboard behavior
    $labels.on("keydown", (e) => {
      // Activate on Enter/Space (5.16)
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        activate(e.currentTarget);
        return;
      }

      // Tab from label -> first link in that category
      if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        activate(e.currentTarget);
        focusFirstInActiveCategory($root);
        return;
      }

      // Shift+Tab from FIRST label -> back to opener
      if (e.key === "Tab" && e.shiftKey) {
        const firstLabel = $labels.get(0);
        if (e.currentTarget === firstLabel) {
          e.preventDefault();
          const triggerEl = $root.get(0).__triggerEl;
          if (triggerEl && typeof triggerEl.focus === "function")
            triggerEl.focus();
        }
      }
    });

    // Category keyboard behavior (this is where the “last link” lives)
    // Category keyboard behavior (capture phase so we don't miss single-link categories)
    const rootEl = $root.get(0);

    rootEl.addEventListener(
      "keydown",
      (e) => {
        if (e.key !== "Tab") return;

        const activeEl = document.activeElement;
        if (!activeEl) return;

        const $activeCat = $targets.filter(":not(.hidden)").first();
        if (!$activeCat.length) return;

        const activeCatEl = $activeCat.get(0);
        if (!activeCatEl.contains(activeEl)) return;

        const focusables = getFocusable(activeCatEl);
        if (!focusables.length) return;

        // Shift+Tab from first link -> go back to active label
        if (e.shiftKey && focusables[0] === activeEl) {
          e.preventDefault();
          focusActiveLabel();
          return;
        }

        // Tab from last link -> go to next label (works even if there is only 1 link)
        if (!e.shiftKey && focusables[focusables.length - 1] === activeEl) {
          const moved = focusNextLabel();
          if (moved) {
            e.preventDefault();
            return;
          }

          // ✅ No next label => end of the whole submenu => close and proceed to next top-level item
          e.preventDefault();
          closeMenuPanelAndFocusNext(rootEl);
        }
      },
      true, // ✅ CAPTURE
    );

    // Init: ensure default-visible category is active
    const initialLabel = $labels.get(0);
    if (initialLabel) activate(initialLabel);
  });
};

/* -------------------------------------------------------------------------- */
/* Mobile behavior (unchanged)                                                 */
/* -------------------------------------------------------------------------- */

export const navMenuAnimatedMobileComponent = async () => {
  $(".nav-menu-item-mobile").each(function () {
    $(this).on("click", function () {
      const targetId = $(this).data("target-id");
      const isOpen = $(this).attr("aria-expanded") === "true";
      window.jQuery("#" + targetId).slideToggle();
      $(this).toggleClass("text-red");
      $(this).attr("aria-expanded", String(!isOpen));
    });
  });

  $(".nav-menu-label-mobile").each(function () {
    $(this).on("click", function () {
      var targetId = $(this).data("target-id");
      const isOpen = $(this).attr("aria-expanded") === "true";
      window.jQuery("#" + targetId).slideToggle();
      $(this).attr("aria-expanded", String(!isOpen));
    });
  });
};

export const headerComponent = async () => {
  await navMenuItemComponent();
  await navMenuAnimatedMobileComponent();
  await navMenuComponent();

  bindNavMenuCloseButtons();

  $(".header-mobile-toggle").on("click", function () {
    $(".header-mobile").toggleClass("hidden flex");

    const $icon = $(this).find(".menu-icon");
    const isOpen = $(this).attr("data-menu-open") === "true";

    if (isOpen) {
      $icon.html(`
        <path d="M3 12H21M3 6H21M9 18H21"
              stroke="black"
              stroke-width="2"
              stroke-linecap="round"/>
      `);
      $(this).attr("data-menu-open", "false");
      $(this).attr("aria-expanded", "false");
    } else {
      $icon.html(`
        <path d="M6 6L18 18M6 18L18 6"
              stroke="black"
              stroke-width="2"
              stroke-linecap="round"/>
      `);
      $(this).attr("data-menu-open", "true");
      $(this).attr("aria-expanded", "true");
    }
  });

  $(".my-wuestenrot").on("click", function () {
    const isOpen = $(this).attr("aria-expanded") === "true";
    $(".my-wuestenrot-menu").toggleClass("hidden flex");
    $(this).attr("aria-expanded", String(!isOpen));
  });
};
